import { AnimationEvent } from '@angular/animations';
import {
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';
import {
  ATX_BREAKPOINT_MEDIA,
  ATX_DEVICE_SIZE,
} from '@epicuro-next/ataraxia/breakpoint';
import { BehaviorSubject, EMPTY, fromEvent, merge, Subject } from 'rxjs';
import {
  auditTime,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

import { DropdownMenuComponent } from '../components/dropdown-menu/dropdown-menu.component';
import { POSITION_MAP } from '../models/position';

export type PlacementType =
  | 'bottomLeft'
  | 'bottomCenter'
  | 'bottomRight'
  | 'topLeft'
  | 'topCenter'
  | 'topRight';

const listOfPositions = [
  POSITION_MAP.bottomLeft,
  POSITION_MAP.bottomRight,
  POSITION_MAP.topRight,
  POSITION_MAP.topLeft,
];

@Directive({
  selector: '[atxDropdown]',
  standalone: false,
})
export class DropdownDirective implements AfterViewInit, OnDestroy, OnChanges {
  private overlayRef: OverlayRef | null = null;
  private portal: TemplatePortal;
  private destroy$: Subject<void> = new Subject();
  private trigger$ = new BehaviorSubject<'click' | 'hover'>('click');
  private overlayClose$ = new BehaviorSubject<boolean>(false);
  private visible = false;
  private positionStrategyMd = this.overlay
    .position()
    .flexibleConnectedTo(this.elementRef.nativeElement)
    .withLockedPosition()
    .withViewportMargin(10)
    .withDefaultOffsetY(5)
    .withTransformOriginOn('.atx-dropdown');
  private positionStrategySm = this.overlay
    .position()
    .global()
    .centerHorizontally()
    .bottom('0px');

  private inputVisible$ = new BehaviorSubject<boolean>(false);

  @Input('atxDropdownMenu')
  public dropdownMenu: DropdownMenuComponent | null = null;

  @Input('atxTrigger')
  public trigger: 'click' | 'hover' = 'click';

  @Input('atxClickHide')
  public clickHide = true;

  @Input('atxDisabled')
  public disabled = false;

  @Input('atxPlacement')
  public placement: PlacementType = 'bottomCenter';

  // tslint:disable-next-line:no-output-rename
  @Output('atxOpened')
  public opened = new EventEmitter<boolean>();

  public get isSm() {
    return window.matchMedia(ATX_BREAKPOINT_MEDIA[ATX_DEVICE_SIZE.XS]).matches;
  }

  private get positionStrategy() {
    if (this.isSm) {
      return this.positionStrategySm;
    }

    return this.positionStrategyMd;
  }

  public get backdropClass() {
    if (this.isSm) {
      return;
    }

    return 'cdk-overlay-transparent-backdrop';
  }

  constructor(
    private elementRef: ElementRef,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2,
  ) {}

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.opened.emit(false);
    }
  }

  public ngAfterViewInit(): void {
    if (!this.dropdownMenu) {
      return;
    }

    const nativeElement: HTMLElement = this.elementRef.nativeElement;

    const hostMouseState$ = merge(
      fromEvent(nativeElement, 'mouseenter').pipe(mapTo(true)),
      fromEvent(nativeElement, 'mouseleave').pipe(mapTo(false)),
    );
    // We create this 3 observables in order to be able dynamically change trigger.
    // Please check ngOnChanges trigger$ and visibleStateByTrigger$
    const menuMouseState$ = this.dropdownMenu.mouseState$;
    const mergedMouseState$ = merge(menuMouseState$, hostMouseState$);
    const hostClickState$ = fromEvent(nativeElement, 'click').pipe(
      map(() => !this.visible),
    );

    const descendantMenuItemClick$ =
      this.dropdownMenu.descendantMenuItemClick$.pipe(
        filter(() => this.clickHide),
        mapTo(false),
      );

    const visibleStateByTrigger$ = this.trigger$.pipe(
      switchMap((trigger) => {
        if (trigger === 'hover') {
          return mergedMouseState$;
        } else if (trigger === 'click') {
          return hostClickState$;
        } else {
          return EMPTY;
        }
      }),
    );

    const domTriggerVisible$ = merge(
      visibleStateByTrigger$,
      descendantMenuItemClick$,
      this.overlayClose$,
    ).pipe(filter(() => !this.disabled));

    merge(domTriggerVisible$, this.inputVisible$)
      .pipe(auditTime(150), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((visible) => this.triggerMenu(visible));

    this.dropdownMenu.animationStateChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: AnimationEvent) => {
        if (event.toState === 'void') {
          if (this.overlayRef) {
            this.overlayRef.dispose();
          }
          this.overlayRef = null;
        }
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    const { disabled, trigger } = changes;

    if (trigger) {
      this.trigger$.next(this.trigger);
    }

    if (disabled) {
      const nativeElement = this.elementRef.nativeElement;
      if (this.disabled) {
        this.renderer.setAttribute(nativeElement, 'disabled', '');
        this.inputVisible$.next(false);
      } else {
        this.renderer.removeAttribute(nativeElement, 'disabled');
      }
    }
  }

  public close() {
    this.inputVisible$.next(false);
  }

  private triggerMenu(visible: boolean) {
    if (!this.overlayRef && !visible) {
      this.opened.emit(false);
      return;
    }

    if (this.overlayRef && !visible) {
      this.overlayRef.detach();

      this.opened.emit(false);
      return;
    }

    const triggerMinWidth =
      this.elementRef.nativeElement.getBoundingClientRect().width;

    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        positionStrategy: this.positionStrategy,
        minWidth: triggerMinWidth,
        disposeOnNavigation: true,
        hasBackdrop: this.trigger === 'click',
        backdropClass: this.backdropClass,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
      });

      merge(
        this.overlayRef.backdropClick(),
        this.overlayRef.detachments(),
        this.overlayRef
          .outsidePointerEvents()
          .pipe(
            filter(
              (e: MouseEvent) =>
                !this.elementRef.nativeElement.contains(e.target),
            ),
          ),
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.overlayClose$.next(false);
        });
    } else {
      const overlayConfig = this.overlayRef.getConfig();
      overlayConfig.minWidth = triggerMinWidth;
    }

    if (!this.isSm) {
      (
        this.positionStrategy as FlexibleConnectedPositionStrategy
      ).withPositions([POSITION_MAP[this.placement], ...listOfPositions]);
    }

    if (
      !this.portal ||
      this.portal.templateRef !== this.dropdownMenu?.templateRef
    ) {
      this.portal = new TemplatePortal(
        (this.dropdownMenu as DropdownMenuComponent).templateRef,
        this.viewContainerRef,
      );
    }

    this.overlayRef.attach(this.portal);
    this.opened.emit(true);
  }
}
